import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Contact, Plus, Search, Tag, UserX, UserCheck, Trash2, Upload, CheckSquare, ArrowLeft, Send } from 'lucide-react'
import { useWaContacts, useWaTags, useDeleteWaContact, useBlockWaContact, useUnblockWaContact } from '@hooks/useWaContacts'
import { useWaCampaign, useAddWaCampaignRecipients } from '@hooks/useWaCampaigns'
import { WaContact, WaTag } from '@/types'
import { cn } from '@lib/utils'
import { CreateWaContactDialog } from '@components/whatsapp/CreateWaContactDialog'
import { ImportWaContactsDialog } from '@components/whatsapp/ImportWaContactsDialog'
import { toast } from 'react-hot-toast'

export function WaContactsPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const addToCampaignId = searchParams.get('addToCampaign')
  const campaignId = addToCampaignId ? Number(addToCampaignId) : null

  const [search, setSearch] = useState('')
  const [selectedTags, setSelectedTags] = useState<number[]>([])
  const [page, setPage] = useState(1)
  const [showBlocked, setShowBlocked] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [selectedContacts, setSelectedContacts] = useState<number[]>([])

  // Campaign data when in add-to-campaign mode
  const { data: campaign } = useWaCampaign(campaignId || 0)
  const addRecipientsMutation = useAddWaCampaignRecipients(campaignId || 0)

  const { data, isLoading, error } = useWaContacts({
    search,
    tagIds: selectedTags.length > 0 ? selectedTags : undefined,
    isBlocked: showBlocked ? true : undefined,
    page,
    limit: 20,
  })

  const { data: tags } = useWaTags()
  const deleteContact = useDeleteWaContact()
  const blockContact = useBlockWaContact()
  const unblockContact = useUnblockWaContact()

  // Clear selection when page changes
  useEffect(() => {
    setSelectedContacts([])
  }, [page, search, selectedTags])

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this contact?')) {
      await deleteContact.mutateAsync(id)
    }
  }

  const handleBlock = async (id: number) => {
    await blockContact.mutateAsync(id)
  }

  const handleUnblock = async (id: number) => {
    await unblockContact.mutateAsync(id)
  }

  const toggleTag = (tagId: number) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    )
    setPage(1)
  }

  const toggleContactSelection = (contactId: number) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    )
  }

  const toggleSelectAll = () => {
    if (!data?.data) return
    const currentPageIds = data.data.filter((c: WaContact) => !c.isBlocked).map((c: WaContact) => c.id)
    const allSelected = currentPageIds.every((id: number) => selectedContacts.includes(id))

    if (allSelected) {
      setSelectedContacts((prev) => prev.filter((id) => !currentPageIds.includes(id)))
    } else {
      setSelectedContacts((prev) => [...new Set([...prev, ...currentPageIds])])
    }
  }

  const handleAddToCampaign = async () => {
    if (!campaignId || selectedContacts.length === 0) return

    try {
      const result = await addRecipientsMutation.mutateAsync({
        recipientIds: selectedContacts,
      })
      toast.success(`Added ${result.added} contacts to campaign`)
      navigate(`/whatsapp/campaigns/${campaignId}`)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add contacts to campaign')
    }
  }

  const isAddToCampaignMode = !!campaignId

  return (
    <div className="space-y-6">
      {/* Campaign Selection Header */}
      {isAddToCampaignMode && campaign && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/whatsapp/campaigns/${campaignId}`)}
                className="rounded-lg p-1 hover:bg-green-100"
              >
                <ArrowLeft size={20} className="text-green-700" />
              </button>
              <div>
                <h2 className="font-semibold text-green-800">
                  Adding contacts to: {campaign.name}
                </h2>
                <p className="text-sm text-green-600">
                  Select contacts below, then click &quot;Add Selected to Campaign&quot;
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-green-700">
                {selectedContacts.length} selected
              </span>
              <button
                onClick={handleAddToCampaign}
                disabled={selectedContacts.length === 0 || addRecipientsMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
                {addRecipientsMutation.isPending ? 'Adding...' : 'Add Selected to Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Contact size={32} className="text-green-600" />
          <h1 className="text-3xl font-bold text-gray-900">WhatsApp Contacts</h1>
        </div>
        {!isAddToCampaignMode && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowImportDialog(true)}
              className="flex items-center gap-2 rounded-lg border border-green-600 px-4 py-2.5 font-medium text-green-600 hover:bg-green-50"
            >
              <Upload size={20} />
              Import CSV
            </button>
            <button
              onClick={() => setShowAddDialog(true)}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 font-medium text-white hover:bg-green-700"
            >
              <Plus size={20} />
              Add Contact
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showBlocked}
            onChange={(e) => {
              setShowBlocked(e.target.checked)
              setPage(1)
            }}
            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
          />
          <span className="text-sm text-gray-700">Show blocked only</span>
        </label>
      </div>

      {/* Tags filter */}
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Tag size={16} className="text-gray-500" />
          {tags.map((tag: WaTag) => (
            <button
              key={tag.id}
              onClick={() => toggleTag(tag.id)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                selectedTags.includes(tag.id)
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
              style={
                selectedTags.includes(tag.id)
                  ? undefined
                  : { backgroundColor: tag.color ? `${tag.color}20` : undefined }
              }
            >
              {tag.name}
            </button>
          ))}
          {selectedTags.length > 0 && (
            <button
              onClick={() => setSelectedTags([])}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            Error loading contacts. Please try again.
          </div>
        ) : !data?.data?.length ? (
          <div className="text-center py-12 text-gray-500">
            No contacts found. Add your first WhatsApp contact!
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {isAddToCampaignMode && (
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        data.data.filter((c: WaContact) => !c.isBlocked).length > 0 &&
                        data.data
                          .filter((c: WaContact) => !c.isBlocked)
                          .every((c: WaContact) => selectedContacts.includes(c.id))
                      }
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Message
                </th>
                {!isAddToCampaignMode && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.data.map((contact: WaContact) => (
                <tr
                  key={contact.id}
                  className={cn(
                    'hover:bg-gray-50',
                    isAddToCampaignMode && selectedContacts.includes(contact.id) && 'bg-green-50'
                  )}
                >
                  {isAddToCampaignMode && (
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => toggleContactSelection(contact.id)}
                        disabled={contact.isBlocked}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-700 font-medium">
                          {(contact.name || contact.phone)[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {contact.name || 'Unknown'}
                        </div>
                        {contact.company && (
                          <div className="text-sm text-gray-500">{contact.company}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {contact.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {contact.tags?.slice(0, 3).map((tag: WaTag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: tag.color ? `${tag.color}20` : '#e5e7eb',
                            color: tag.color || '#374151',
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                      {contact.tags && contact.tags.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{contact.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {contact.isBlocked ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Blocked
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.lastMessageAt
                      ? new Date(contact.lastMessageAt).toLocaleDateString()
                      : 'Never'}
                  </td>
                  {!isAddToCampaignMode && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {contact.isBlocked ? (
                          <button
                            onClick={() => handleUnblock(contact.id)}
                            className="p-1 text-green-600 hover:text-green-800"
                            title="Unblock"
                          >
                            <UserCheck size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBlock(contact.id)}
                            className="p-1 text-orange-600 hover:text-orange-800"
                            title="Block"
                          >
                            <UserX size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {(page - 1) * 20 + 1} to{' '}
            {Math.min(page * 20, data.pagination.total)} of {data.pagination.total} contacts
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
              className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <CreateWaContactDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={() => setPage(1)}
      />
      <ImportWaContactsDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onSuccess={() => setPage(1)}
      />
    </div>
  )
}
